/**
 * PlanRateCardDrawer — the card a plan prices from, opened from the header's Rates fact
 * (RP-6.6, RP-12.6, RP-12.8).
 *
 * Answers one question: "what rates is this plan quoting, and why?" — the tier that picks
 * the Rate Basis, the Account's negotiated rate (or list) for every Labor Category, what the
 * plan's own lines actually charge, and each category's rates over time.
 *
 * The tier lever moves the ACCOUNT's tier (product decision, 2026-10-17); the confirm step
 * (PlanTierChangeDialog) says what that does to this plan before anything is written.
 * Negotiated rates are edited on the Account page, linked from here — a new negotiated rate
 * does not reach an existing plan, so editing one from inside a plan would look broken.
 *
 * TERMINOLOGY: Engagement Standard Tier picks a Rate Basis; both are always shown together
 * ("Strategic → Enterprise rates") because they are easy to conflate. "List rate", never
 * "Standard rate" — Standard is also the name of a basis.
 */

import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, ExternalLink, X } from "lucide-react";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { PlanTierChangeDialog } from "@/components/plans/PlanTierChangeDialog";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type PlanRateCardCategoryView,
  type PlanRateCardView,
  usePlanRateCard,
  usePlanRateHistory,
} from "@/hooks/use-plan-rate-card";
import { useSession } from "@/lib/auth-client";
import { canSeeSettingsTab } from "@/lib/nav-access";
import { formatDateOnly, formatRateDelta, type RateHistoryEntry } from "@/lib/plan-rate-card";
import { cn } from "@/lib/utils";

function money(value: string | null | undefined): string {
  return value === null || value === undefined ? "—" : `$${value}`;
}

function dateRange(start: string, end: string | null): string {
  return `${formatDateOnly(start)} – ${end === null ? "open" : formatDateOnly(end)}`;
}

export function PlanRateCardDrawer({ planId, onClose }: { planId: string; onClose: () => void }) {
  const query = usePlanRateCard(planId);
  const [pendingTierId, setPendingTierId] = useState<string | null>(null);
  const { data: session } = useSession();
  const canSeeRateCard = canSeeSettingsTab(session?.user?.role, "/dashboard/settings/rate-card");

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      // The confirm dialog owns Escape while it is open.
      if (e.key === "Escape" && pendingTierId === null) onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose, pendingTierId]);

  return (
    <>
      <button
        type="button"
        aria-label="Close Rate Card"
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      <aside
        aria-label="Rate Card"
        className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-[760px] flex-col border-l border-border bg-background shadow-xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <Eyebrow>Rate Card</Eyebrow>
            <h2 className="mt-1 font-display text-lg font-bold text-heading">
              {query.data?.headline ?? "Loading…"}
            </h2>
            {/* The plan page's one link to the Rate Card lives here, beside the card it
                governs — not as a header button ("Say it once"). SA does not see the Rate
                Card tab (2026-10-20), so the link is hidden for them too. */}
            {canSeeRateCard && (
              <Link
                to="/dashboard/settings/rate-card"
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                Open Rate Card settings
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {query.isLoading && (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          )}
          {query.isError && (
            <ErrorState
              message={query.error instanceof Error ? query.error.message : "Failed to load"}
              onRetry={() => query.refetch()}
            />
          )}
          {query.data && (
            <DrawerBody card={query.data} onChooseTier={(tierId) => setPendingTierId(tierId)} />
          )}
        </div>
      </aside>

      {query.data && pendingTierId !== null && (
        <PlanTierChangeDialog
          card={query.data}
          tierId={pendingTierId}
          onClose={() => setPendingTierId(null)}
        />
      )}
    </>
  );
}

function DrawerBody({
  card,
  onChooseTier,
}: {
  card: PlanRateCardView;
  onChooseTier: (tierId: string) => void;
}) {
  return (
    <div className="space-y-5">
      <TierSection card={card} onChooseTier={onChooseTier} />

      <section className="space-y-2">
        <div className="flex items-end justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {!card.canViewAccountRates
              ? `${card.clientAccountName}'s negotiated rates are visible to its Account Managers, Finance and Resource Managers. The plan's own rates are shown.`
              : card.accountCard === null
                ? `${card.clientAccountName} has no negotiated rate that applies to this plan, so every Labor Category is at list.`
                : `${card.accountCard.appliedCount} negotiated ${card.accountCard.appliedCount === 1 ? "rate applies" : "rates apply"}, as they stood when the plan was created. The rest are at list.`}
          </p>
          {card.canManageRates && (
            <Link
              to="/dashboard/accounts/$id"
              params={{ id: card.clientAccountId }}
              className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
            >
              Manage negotiated rates
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
        <CategoryTable card={card} />
      </section>
    </div>
  );
}

function TierSection({
  card,
  onChooseTier,
}: {
  card: PlanRateCardView;
  onChooseTier: (tierId: string) => void;
}) {
  const where =
    card.rateMode === "ClientDictated"
      ? "This plan uses Client-Dictated rates, so no Rate Card prices it."
      : card.fixedStaffed
        ? "The Solution is Fixed-Staffed, so this plan prices on Fixed rates whatever the Engagement Standard Tier."
        : card.effectiveTier.source === "Plan"
          ? `This plan is priced on its own Engagement Standard Tier, ${card.effectiveTier.name}.`
          : card.effectiveTier.source === "Solution"
            ? `The Solution has its own Engagement Standard Tier, ${card.effectiveTier.name}, which outranks the Account's.`
            : `Priced on ${card.clientAccountName}'s Engagement Standard Tier.`;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            Engagement Standard Tier
          </dt>
          <dd>
            {card.canChangeTier ? (
              <Select
                value={card.accountTier.id}
                onValueChange={(id) => {
                  if (id !== card.accountTier.id) onChooseTier(id);
                }}
              >
                <SelectTrigger
                  className="h-9 w-full"
                  aria-label={`${card.clientAccountName} Engagement Standard Tier`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {card.tiers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} → {t.rateBasisName} rates
                      {!t.rateBasisUsable && " (no rates yet)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-sm font-medium">
                {card.accountTier.name} →{" "}
                {card.tiers.find((t) => t.id === card.accountTier.id)?.rateBasisName ?? "—"} rates
              </span>
            )}
          </dd>
        </div>
        <div className="space-y-1">
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            This plan's Rate Basis
          </dt>
          <dd className="text-sm font-medium">{card.rateBasisLabel}</dd>
        </div>
      </dl>
      <p className="mt-3 text-sm text-muted-foreground">
        {where}{" "}
        {card.canChangeTier &&
          (card.planStatus === "Draft"
            ? "Changing it moves the Account, and re-prices this Open plan."
            : `Changing it moves the Account for new plans; this ${card.planStatus} plan keeps its rates.`)}
      </p>
    </section>
  );
}

function CategoryTable({ card }: { card: PlanRateCardView }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  // An [ACCT] outside the Account sees list rates and the plan's own — not the negotiated
  // card (RP-12.6) — so its four columns go rather than reading "At list" untruthfully.
  const negotiated = card.canViewAccountRates;
  const columnCount = 3 + (card.canViewCost ? 1 : 0) + (negotiated ? 4 : 0);

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left">
            <Th>Labor Category</Th>
            <Th right>List rate</Th>
            {negotiated && <Th right>Negotiated rate</Th>}
            {negotiated && <Th right>Delta</Th>}
            <Th right>On this plan</Th>
            {card.canViewCost && <Th right>Cost</Th>}
            {negotiated && <Th>Effective</Th>}
            {negotiated && <Th>Source</Th>}
          </tr>
        </thead>
        <tbody>
          {card.categories.map((category) => {
            const open = expanded === category.laborCategoryId;
            return (
              <Fragment key={category.laborCategoryId}>
                <CategoryRow
                  category={category}
                  canViewCost={card.canViewCost}
                  negotiated={negotiated}
                  open={open}
                  onToggle={() => setExpanded(open ? null : category.laborCategoryId)}
                />
                {open && (
                  <tr className="border-b border-border bg-muted/20">
                    <td colSpan={columnCount} className="px-4 py-3">
                      <RateHistory
                        planId={card.planId}
                        laborCategoryId={category.laborCategoryId}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return (
    <th
      className={cn(
        "px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
        right && "text-right",
      )}
    >
      {children}
    </th>
  );
}

function CategoryRow({
  category,
  canViewCost,
  negotiated,
  open,
  onToggle,
}: {
  category: PlanRateCardCategoryView;
  canViewCost: boolean;
  negotiated: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const account = category.accountRate;
  const delta = formatRateDelta(category.listRate, account?.billRate ?? null);
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/20">
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 text-left font-medium hover:text-primary"
          title="Show this Labor Category's rates over time"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          {category.laborCategoryName}
          {!category.active && (
            <Badge variant="outline" className="text-xs">
              Inactive
            </Badge>
          )}
        </button>
      </td>
      <td
        className={cn(
          "px-3 py-2 text-right tabular-nums",
          account && "text-muted-foreground line-through",
        )}
      >
        {money(category.listRate)}
      </td>
      {negotiated && (
        <>
          <td className="px-3 py-2 text-right tabular-nums">
            {account ? (
              <span className="font-semibold text-primary" title={`Reason: ${account.reason}`}>
                {money(account.billRate)}
              </span>
            ) : (
              <span className="text-muted-foreground">At list</span>
            )}
          </td>
          <td className="px-3 py-2 text-right tabular-nums">
            {delta === null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <span
                className={cn(
                  "whitespace-nowrap font-medium",
                  delta.direction === "discount" && "text-warning-700",
                  delta.direction === "premium" && "text-success",
                  delta.direction === "level" && "text-muted-foreground",
                )}
              >
                {delta.text}
              </span>
            )}
          </td>
        </>
      )}
      <td
        className="px-3 py-2 text-right tabular-nums"
        title={
          category.customLineCount > 0
            ? `${category.customLineCount} of ${category.lineCount} ${category.lineCount === 1 ? "Plan Line carries" : "Plan Lines carry"} a custom rate.`
            : undefined
        }
      >
        {category.lineCount === 0 ? (
          <span className="text-muted-foreground">Not used</span>
        ) : (
          <span className={cn(category.customLineCount > 0 && "text-primary")}>
            {category.ratesOnPlan.map(money).join(" / ")}
          </span>
        )}
      </td>
      {canViewCost && (
        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
          {money(category.avgInternalCost)}
        </td>
      )}
      {negotiated && (
        <>
          <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
            {account ? dateRange(account.effectiveStart, account.effectiveEnd) : "—"}
          </td>
          <td
            className="max-w-[160px] truncate px-3 py-2 text-muted-foreground"
            title={account?.sourceDocumentReference ?? undefined}
          >
            {account?.sourceDocumentReference ?? "—"}
          </td>
        </>
      )}
    </tr>
  );
}

const STATUS_LABEL: Record<RateHistoryEntry["status"], string> = {
  current: "Current",
  past: "Past",
  scheduled: "Scheduled",
  replaced: "Edited",
  removed: "Removed",
};

function RateHistory({ planId, laborCategoryId }: { planId: string; laborCategoryId: string }) {
  const history = usePlanRateHistory(planId, laborCategoryId, true);

  if (history.isLoading) return <Skeleton className="h-16 w-full" />;
  if (history.isError) {
    return (
      <ErrorState
        message={history.error instanceof Error ? history.error.message : "Failed to load"}
        onRetry={() => history.refetch()}
      />
    );
  }
  if (!history.data || history.data.entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No rates recorded yet.</p>;
  }

  return (
    <div className="space-y-2">
      <Eyebrow>Rates over time · {history.data.rateBasisLabel}</Eyebrow>
      <ul className="space-y-1.5">
        {history.data.entries.map((e) => (
          <li
            key={`${e.source}-${e.effectiveStart}-${e.billRate}-${e.status}-${e.changedAt ?? ""}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
          >
            <Badge
              variant={e.status === "current" ? "secondary" : "outline"}
              className="w-[84px] justify-center text-xs"
            >
              {STATUS_LABEL[e.status]}
            </Badge>
            <span className="w-[72px] font-medium">
              {e.source === "Card" ? "List" : "Negotiated"}
            </span>
            <span
              className={cn(
                "w-[80px] text-right tabular-nums font-semibold",
                (e.status === "replaced" || e.status === "removed") &&
                  "text-muted-foreground line-through",
              )}
            >
              {money(e.billRate)}
            </span>
            <span className="text-muted-foreground">
              {dateRange(e.effectiveStart, e.effectiveEnd)}
              {e.source === "Account" && e.basisLabel === "every basis" && " · every basis"}
              {e.changedAt !== null &&
                ` · ${e.status === "removed" ? "removed" : "changed"} ${formatDateOnly(e.changedAt)}`}
              {e.sourceDocumentReference && ` · ${e.sourceDocumentReference}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
